import { useCallback, useState } from 'react';
import { LinkProps, useRouter } from '@tanstack/react-router';

import { useAuth } from '@features/auth/hooks/useAuth';
import { createLoginLink } from '@features/auth/utils/redirect';
import { ClubRulesModal } from '@features/chessrussian/customizations/club-rules/ClubRulesModal';
import {
  GRAND_PRIX_PROMO_PAGE,
  PREMIUM_ACCOUNT_PAGE,
  PREMIUM_RCF_PROMO_PAGE,
} from '@features/chessrussian/customizations/constants';
import {
  isGrandPrixEvent,
  isPremiumEvent,
  isPremiumRcfEvent,
} from '@features/chessrussian/customizations/utils/event-filters';
import { isChessRussianClub } from '@features/chessrussian/customizations/utils/isChessRussianClub';
import { useAcceptClubRulesMutation, useClubCardQuery } from '@features/club/api/hooks';
import { useParticipantActionTournamentMutation } from '@features/tournament/api/hooks';
import { useTournamentParticipantState } from '@features/tournament/hooks/useTournamentParticipantState';
import { useTournamentCtx } from '@features/tournament/providers/TournamentProvider';
import { TournamentParticipationButton } from '@features/tournament/ui/components/TournamentParticipationButton/TournamentParticipationButton';

export function TournamentParticipationButtonWidget() {
  const { state } = useTournamentCtx();
  const { tournament, clubId, tournamentId } = state;
  const { mutateAsync: onJoin, isPending: isJoinPending } = useParticipantActionTournamentMutation(
    'join',
    clubId,
    tournamentId,
  );
  const { mutateAsync: onLeave, isPending: isLeavePending } =
    useParticipantActionTournamentMutation('leave', clubId, tournamentId);
  const { data: club } = useClubCardQuery(clubId, {
    staleTime: Infinity,
  });
  const { mutateAsync: acceptRules, isPending: isAcceptPending } =
    useAcceptClubRulesMutation(clubId);
  const router = useRouter();
  const { isAuthenticated } = useAuth();
  const [rulesOpen, setRulesOpen] = useState(false);

  const onJoinAnonymous = useCallback(() => {
    const back = {
      to: '/club/$clubId/tournament/$tournamentId',
      params: { clubId, tournamentId },
    } satisfies LinkProps;

    window.location.href = createLoginLink(router.buildLocation(back).href);
  }, [clubId, router, tournamentId]);

  const needsRulesConsent = !!club?.rulesConsentRequired && !club?.rulesConsentGiven;

  const onJoinAuthenticated = useCallback(async () => {
    if (needsRulesConsent) {
      setRulesOpen(true);
      return;
    }
    await onJoin();
  }, [needsRulesConsent, onJoin]);

  const onConsent = useCallback(async () => {
    try {
      await acceptRules();
    } catch {
      setRulesOpen(false);
      return;
    }
    setRulesOpen(false);
    onJoin();
  }, [acceptRules, onJoin]);

  const { canJoin, canLeave, isBeingPreModerated, canJoinWithTicket } =
    useTournamentParticipantState(tournament);

  if (!tournament) return null;

  const isPremium = isChessRussianClub(clubId) && isPremiumEvent(tournament);
  const isGrandPrix = isPremium && isGrandPrixEvent(tournament);
  const isPremiumRcf = isPremium && isPremiumRcfEvent(tournament);

  return (
    <>
      <TournamentParticipationButton
        onLeave={onLeave}
        onJoin={isAuthenticated ? onJoinAuthenticated : onJoinAnonymous}
        canJoin={canJoin}
        canJoinWithTicket={canJoinWithTicket}
        joinWithTicketLink={
          isGrandPrix
            ? GRAND_PRIX_PROMO_PAGE
            : isPremiumRcf
              ? PREMIUM_RCF_PROMO_PAGE
              : isPremium
                ? PREMIUM_ACCOUNT_PAGE
                : tournament.paymentSettings?.paymentLink
        }
        canLeave={canLeave}
        isBeingPreModerated={isBeingPreModerated}
        participantStatus={tournament.participationStatus}
        isPending={isJoinPending || isLeavePending || isAcceptPending || (isAuthenticated && !club)}
        isMatch={tournament.system === 'OneOnOne' || tournament.system === 'Seance'}
      />
      {rulesOpen && (
        <ClubRulesModal
          onClose={() => setRulesOpen(false)}
          onConsent={onConsent}
          isPending={isAcceptPending}
        />
      )}
    </>
  );
}
